import React, { useContext } from 'react';

import UIContext from 'components/Utils/UIContext';
import { faClone, faClose, faEdit, faEyeSlash, faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionButton from "components/action/ActionButton";

import { useTranslation } from "react-i18next";

import "./IndicatorGeneralTab.sass";
import { Button, Tag, Tooltip, Flex } from 'antd';
import { keyBy, sortBy } from 'lodash';
import { ImpactRelationSection } from 'components/tabs/ImpactRelationSection';
import { FishboneLinksReadonly } from 'components/tabs/FishboneLinksReadonly';
import { useIndicatorImpactController } from 'hooks/useIndicatorImpactController';
import { MiniSlider } from 'components/controls/MiniSlider';
import { getItemAlteredByScenario } from 'utils/rules';

interface IndicatorGeneralTabProps {
    selection: any;
    onEdit: Function;
    onClone: Function;
    onClose: Function;
    onDisable: Function;
    onDelete: Function;
    scenario: any;
    scenarios?: any[];
    // Réajuste la valeur (trend) de l'indicateur sélectionné (slider).
    onUpdateTrend?: (value: string) => void;

    indicators: any[];
    risks?: any[];
    stakes?: any[];
    fishboneBranchs?: any[];
}

export const IndicatorGeneralTab = (props: IndicatorGeneralTabProps) => {

    const { t } = useTranslation();
    const uiContext = useContext(UIContext);
    const { createRiskIndicatorImpact, updateRiskIndicatorImpactStrength, deleteRiskIndicatorImpact, createStakeIndicatorImpact, updateStakeIndicatorImpactStrength, deleteStakeIndicatorImpact } = useIndicatorImpactController();

    return (
        <div className="IndicatorGeneralTab">

            <Flex vertical>

                <Flex className="mb-1">
                    <div className="w-100 text-left">
                        <div className="title">{props.selection?.name}</div>
                        <small>{t("models.indicator.name")}</small>
                    </div>
                    <Tooltip title={t('actions.close')} placement='left'>

                        <Button
                            shape="circle"
                            variant="solid"
                            className="mini ml-auto"
                            tooltip={t('models.item.actions.edit')}
                            style={{ marginRight: '-0.9rem' }}
                            onClick={props.onClose}
                        >
                            <FontAwesomeIcon icon={faClose} />
                        </Button></Tooltip>
                </Flex>

                <ul>
                    {props.selection?.description && (
                        <li className="EditableListItem indicator-description">
                            <div>{props.selection?.description}</div>
                        </li>
                    )}
                </ul>

                {uiContext.canEdit && <div className="Actions mt-1 mb-1 pl-1 text-center ml-auto mr-auto" data-testid="indicator-actions">
                    <ActionButton
                        className="small ml-05"
                        tooltip={t('actions.edit')}
                        data-testid="action-edit"
                        onClick={() => props.onEdit(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </ActionButton>

                    {false && <ActionButton
                        className="small"
                        tooltip={t('actions.clone')}
                        data-testid="action-clone"
                        onClick={() => props.onClone(props.selection)}
                    >
                        <FontAwesomeIcon icon={faClone} />
                    </ActionButton>}

                    {props.onDisable && <ActionButton
                        className="small"
                        tooltip={t('actions.disable')}
                        data-testid="action-disable"
                        onClick={() => props.onDisable(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEyeSlash} />
                    </ActionButton>}

                    {props.onDelete && <ActionButton
                        className="small"
                        tooltip={t('actions.delete')}
                        data-testid="action-delete"
                        onClick={() => props.onDelete(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </ActionButton>}
                </div>}

                {/* Réajustement de la valeur (slider), sous les boutons
                    d'édition. Marqueurs = valeurs par scénario. Pierre. */}
                {props.onUpdateTrend && props.selection && (
                    <div className="mt-2 mb-1 pr-1 pl-1">
                        <MiniSlider
                            key={props.selection.id}
                            value={getItemAlteredByScenario(props.selection, props.scenario)?.trend}
                            min={0}
                            max={100}
                            style={{ width: '100%' }}
                            readOnly={!uiContext.canEdit}
                            onChange={(val: string) => props.onUpdateTrend && props.onUpdateTrend(val)}
                            additionalMarkers={((props.scenarios || []).concat([{ id: '', name: t('models.scenario.baseline') }])).map((sc: any) => ({
                                id: sc.id,
                                name: sc.name || t('models.scenario.baseline'),
                                value: getItemAlteredByScenario(props.selection, sc.id ? sc : null)?.trend
                            }))}
                        />
                    </div>
                )}

                <FishboneLinksReadonly
                    title={t('pages.fishbone.title')}
                    links={props.selection?.fishboneLinks}
                    fishboneBranchs={props.fishboneBranchs}
                />

                <ImpactRelationSection
                    title={t('models.item.fields.stakes.name')}
                    items={props.stakes || []}
                    scenario={props.scenario}
                    emptyMessage={t('models.stake.no_x')}
                    getLink={(s) =>
                        (s.impactedIndicators || []).find(
                            (x: any) => x.indicatorId === props.selection?.id
                        )
                    }
                    onToggle={(s, link) => {
                        if (!link || !link.id) {
                            createStakeIndicatorImpact(s.id, props.selection?.id);
                        } else if (link.strength < 3) {
                            updateStakeIndicatorImpactStrength(link, link.strength + 1);
                        } else {
                            deleteStakeIndicatorImpact(link.id, s.id);
                        }
                    }}
                />

                <ImpactRelationSection
                    title={t('models.item.fields.risks.name')}
                    items={(props.risks || []).filter((r: any) => !r.isOpportunity)}
                    scenario={props.scenario}
                    emptyMessage={t('models.risk.messages.no_x')}
                    getLink={(r) =>
                        (r.impactedIndicators || []).find(
                            (x: any) => x.indicatorId === props.selection?.id
                        )
                    }
                    onToggle={(r, link) => {
                        if (!link || !link.id) {
                            createRiskIndicatorImpact(r.id, props.selection?.id);
                        } else if (link.strength < 3) {
                            updateRiskIndicatorImpactStrength(link, link.strength + 1);
                        } else {
                            deleteRiskIndicatorImpact(link.id, r.id);
                        }
                    }}
                />

                <ImpactRelationSection
                    title={t('models.item.fields.opportunities.name')}
                    items={(props.risks || []).filter((r: any) => !!r.isOpportunity)}
                    scenario={props.scenario}
                    emptyMessage={t('models.risk.messages.no_x_positive')}
                    getLink={(r) =>
                        (r.impactedIndicators || []).find(
                            (x: any) => x.indicatorId === props.selection?.id
                        )
                    }
                    onToggle={(r, link) => {
                        if (!link || !link.id) {
                            createRiskIndicatorImpact(r.id, props.selection?.id);
                        } else if (link.strength < 3) {
                            updateRiskIndicatorImpactStrength(link, link.strength + 1);
                        } else {
                            deleteRiskIndicatorImpact(link.id, r.id);
                        }
                    }}
                />

            </Flex>
        </div>
    );
}